// frontend/src/components/SucursalSelector.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Modal de selección de sucursal activa para el dispositivo.
//
// CUÁNDO SE MUESTRA:
//   1. Después del login, si localStorage no tiene sucursal_id guardado.
//   2. Desde el Sidebar (botón "Cambiar Sucursal") para reasignar el dispositivo.
//
// FLUJO:
//   Login → authAPI devuelve { user, token, sucursales[] }
//   → Si !hasSucursalAsignada() → mostrar este componente
//   → Operador elige su sucursal → setSucursalActiva(suc) → cierra el modal
//   → Toda venta offline desde ahora lleva sucursal_id automáticamente
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { sucursalesAPI } from '../services/api';
import { setSucursalActiva, clearSucursalActiva, isCajero, isAdmin, persistCajeroSession, getSucursalesPermitidas } from '../hooks/useSucursal';
import './SucursalSelector.css';

/**
 * @param {object}   props
 * @param {Array}    props.sucursales    - Lista pre-cargada (del login). Si no se pasa, las fetcha.
 * @param {string}   props.businessName  - Nombre del negocio para mostrar en el badge
 * @param {function} props.onSelect      - Callback cuando el operador elige una sucursal
 * @param {boolean}  props.allowClose    - Si true, muestra botón X para cerrar (modo "cambiar")
 * @param {function} props.onClose       - Callback para cerrar (sólo si allowClose=true)
 */
const SucursalSelector = ({
  sucursales: sucursalesProp = null,
  businessName = 'Mi Negocio',
  onSelect,
  allowClose = false,
  onClose,
}) => {
  const [sucursales, setSucursales] = useState(sucursalesProp || []);
  const [loading, setLoading]       = useState(!sucursalesProp);
  const [error, setError]           = useState('');

  // Si no se pasaron sucursales como prop, las fetchamos
  useEffect(() => {
    if (sucursalesProp) {
      setSucursales(sucursalesProp);
      setLoading(false);
      return;
    }
    const fetchSucursales = async () => {
      try {
        const res = await sucursalesAPI.getAll();
        setSucursales(res.data?.data?.sucursales || []);
      } catch (e) {
        setError('No se pudieron cargar las sucursales. Verifica tu conexión.');
      } finally {
        setLoading(false);
      }
    };
    fetchSucursales();
  }, [sucursalesProp]);

  const handleSelect = (sucursal) => {
    if (isCajero()) {
      persistCajeroSession({
        sucursal_id: sucursal.id,
        sucursal_nombre: sucursal.nombre,
        es_offline: sucursal.es_offline,
        sucursal_modules: sucursal.modules,
        nombre: localStorage.getItem('empleado_nombre') || '',
        rol: localStorage.getItem('empleado_rol') || 'vendedora',
        sucursales_permitidas: getSucursalesPermitidas()
      });
    } else {
      // Guardar en localStorage — a partir de aquí todas las ventas llevan este sucursal_id
      setSucursalActiva({
        id:         sucursal.id,
        nombre:     sucursal.nombre,
        es_offline: sucursal.es_offline,
        modules:    sucursal.modules,
      });
    }
    onSelect?.(sucursal);
  };

  // Solo disponible en modo "cambiar sucursal" para Dueños
  const handleSelectTodas = () => {
    clearSucursalActiva();
    onSelect?.(null);
  };

  return (
    <div className="sucursal-overlay" onClick={allowClose ? onClose : undefined}>
      <div className="sucursal-card" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="sucursal-header">
          <div className="sucursal-icon">🏪</div>
          <h2>Selecciona tu sucursal</h2>
          <p>¿Desde qué botica estás operando este dispositivo?</p>
        </div>

        {/* Badge de la empresa */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <div className="sucursal-tenant">
            <span className="sucursal-tenant-dot" />
            {businessName}
          </div>
        </div>

        {/* Contenido */}
        {loading && (
          <div className="sucursal-loading">
            <div className="sucursal-spinner" />
            <span>Cargando sucursales...</span>
          </div>
        )}

        {!loading && error && (
          <div className="sucursal-empty">
            <div className="sucursal-empty-icon">⚠️</div>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && sucursales.length === 0 && (
          <div className="sucursal-empty">
            <div className="sucursal-empty-icon">🏗️</div>
            <p>
              Aún no hay sucursales registradas.<br />
              Pide al administrador que las cree en el sistema.
            </p>
          </div>
        )}

        {!loading && !error && sucursales.length > 0 && (
          <div className="sucursal-list">
            {allowClose && isAdmin() && (
              <button
                className="sucursal-option"
                onClick={handleSelectTodas}
                id="sucursal-btn-todas"
                title="Ver todas las sucursales (sin restringir a ninguna en particular)"
              >
                <div className="sucursal-option-icon online">🌐</div>
                <div className="sucursal-option-info">
                  <div className="sucursal-option-nombre">Todas las sucursales</div>
                  <span className="sucursal-option-badge online">● Sin restricción de módulos</span>
                </div>
                <span className="sucursal-option-arrow">›</span>
              </button>
            )}
            {sucursales.map((suc) => {
              const isOffline = suc.es_offline;
              return (
                <button
                  key={suc.id}
                  className="sucursal-option"
                  onClick={() => handleSelect(suc)}
                  id={`sucursal-btn-${suc.id}`}
                  title={`Seleccionar ${suc.nombre}`}
                >
                  {/* Ícono */}
                  <div className={`sucursal-option-icon ${isOffline ? 'offline' : 'online'}`}>
                    {isOffline ? '📴' : '🌐'}
                  </div>

                  {/* Info */}
                  <div className="sucursal-option-info">
                    <div className="sucursal-option-nombre">{suc.nombre}</div>
                    <span className={`sucursal-option-badge ${isOffline ? 'offline' : 'online'}`}>
                      {isOffline ? '● Modo Offline' : '● Siempre Online'}
                    </span>
                  </div>

                  {/* Flecha */}
                  <span className="sucursal-option-arrow">›</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="sucursal-footer">
          Esta elección se guarda en este dispositivo. Puedes cambiarla desde el menú lateral.
        </div>

        {/* Botón de cerrar (modo "cambiar sucursal" desde el sidebar) */}
        {allowClose && (
          <button
            onClick={onClose}
            style={{
              marginTop: 12,
              width: '100%',
              padding: '10px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              color: '#64748b',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
};

export default SucursalSelector;
